/**
 * Dragon HQ HTTP client.
 *
 * Owns the token/session plumbing (`setAccessToken`, `getAccessToken`, the
 * shared `request<T>` helper) plus the small set of Core endpoints: login,
 * TOTP, enrolment metadata. `request` is exported so vault code can reuse
 * the same 401-handling + Authorization shape without duplicating it.
 * Vault-specific endpoints live in `packages/client/src/apps/vault/lib/api.ts`.
 *
 * A handful of vault-related endpoints (`setKeypair`, `bootstrapVault`,
 * `myEnrolment`) sit here because the Core session store (`core/lib/vault.ts`)
 * calls them during Master Code setup.
 */
import type {
  EncryptedPrivateKey,
  LoginResponse,
  VaultLoginState,
} from '@dragonhq/protocol';

const BASE = import.meta.env.VITE_API_BASE ?? '';

let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
const tokenListeners = new Set<(token: string | null) => void>();

/**
 * Decode the JWT payload's `exp` claim (seconds since epoch). Returns null
 * for malformed / unsigned / unparseable tokens. We never trust this for
 * anything more than client-side scheduling — the server is authoritative.
 */
export function getTokenExpSeconds(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  const payloadPart = parts[1];
  if (parts.length !== 3 || !payloadPart) return null;
  try {
    const payload = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True if the in-memory token has an `exp` claim that is in the past. */
export function isTokenExpired(skewSeconds = 0): boolean {
  const exp = getTokenExpSeconds(getAccessToken());
  if (exp == null) return false;
  return exp - skewSeconds <= Math.floor(Date.now() / 1000);
}

/** Subscribe to token rotations (set / clear / refresh). */
export function subscribeToken(cb: (token: string | null) => void): () => void {
  tokenListeners.add(cb);
  return () => tokenListeners.delete(cb);
}

function notifyTokenListeners(token: string | null) {
  for (const cb of tokenListeners) {
    try { cb(token); } catch (err) { console.warn('[api] token listener threw', err); }
  }
}

export function setAccessToken(token: string | null) {
  accessToken = token;
  if (token) localStorage.setItem('dragonhq.token', token);
  else localStorage.removeItem('dragonhq.token');
  scheduleTokenRefresh(token);
  notifyTokenListeners(token);
}

export function getAccessToken(): string | null {
  if (accessToken) return accessToken;
  accessToken = localStorage.getItem('dragonhq.token');
  // Boot path: a token loaded from localStorage hasn't armed a refresh
  // timer yet. Arm one now so a long-lived tab refreshes before expiry.
  if (accessToken) scheduleTokenRefresh(accessToken);
  return accessToken;
}

/**
 * Schedule a JWT refresh ~60 s before expiry. The 7-day trusted-device JWT
 * still benefits — if the tab stays open across a week the timer fires
 * once. Cancels any previously-scheduled timer. Does nothing for tokens
 * with no `exp` claim or already-expired tokens (the next request will
 * surface 401 and route to sign-in).
 */
function scheduleTokenRefresh(token: string | null) {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (!token) return;
  const exp = getTokenExpSeconds(token);
  if (exp == null) return;
  const nowSec = Math.floor(Date.now() / 1000);
  // Refresh 60 s before expiry, but never sooner than 5 s from now (avoids
  // a tight loop if the server hands us a near-expired token), and never
  // longer than 24 h ahead (re-arm daily so clock skew doesn't strand us).
  const targetSec = Math.max(nowSec + 5, Math.min(exp - 60, nowSec + 24 * 60 * 60));
  const delayMs = (targetSec - nowSec) * 1000;
  refreshTimer = setTimeout(() => {
    void refreshAccessToken().catch((err) => {
      console.warn('[api] proactive refresh failed', err);
    });
  }, delayMs);
}

/**
 * Hard cap on the refresh round-trip. Without this, a captive-portal gateway
 * that accepts the connection but never replies will hang the unlock screen
 * (or any 401-recovery flush) indefinitely. 8 s is long enough for a slow
 * 3G round-trip, short enough that a hang is recoverable without a tab
 * reload.
 */
const REFRESH_TIMEOUT_MS = 8_000;

/**
 * Exchange the current JWT for a fresh one with the same trust level. On
 * 401 (token already expired / revoked), clears the token; the next API
 * call will surface to the user as a sign-in prompt. On timeout / network
 * error, the existing token is preserved and `false` is returned — callers
 * that need to distinguish "definitive 401" from "couldn't reach the
 * server" check `getAccessToken()` afterward.
 */
export async function refreshAccessToken(): Promise<boolean> {
  const tok = getAccessToken();
  if (!tok) return false;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REFRESH_TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${tok}` },
      signal: ctrl.signal,
    });
    if (res.status === 401) {
      setAccessToken(null);
      return false;
    }
    if (!res.ok) throw new Error(`refresh failed: ${res.status}`);
    const body = (await res.json()) as { token: string };
    if (typeof body?.token !== 'string') throw new Error('refresh response missing token');
    setAccessToken(body.token);
    return true;
  } catch (err) {
    console.warn('[api] refreshAccessToken', err);
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * JSON-in / JSON-out fetch wrapper with bearer-token injection.
 *
 * Exported so each app's `lib/api.ts` can reuse the same helper — avoids
 * every app re-implementing 401 detection + `Authorization` header + the
 * empty-body content-type dodge. For multipart/octet endpoints (e.g. attachment
 * upload/download), callers use the raw `fetch` + `getAccessToken()` pattern.
 *
 * `keepalive: true` opts the underlying fetch into surviving page-unload —
 * required for the pagehide-flush path so a final snapshot PUT doesn't get
 * cancelled when the tab closes. Browser caps the body at 64 KB; the IDB
 * mirror + boot recovery handle the rare oversize case.
 */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // Only claim JSON when we're actually sending a body — Fastify rejects empty
  // bodies with Content-Type: application/json (FST_ERR_CTP_EMPTY_JSON_BODY).
  if (init.body != null) headers.set('Content-Type', 'application/json');
  const tok = getAccessToken();
  if (tok) headers.set('Authorization', `Bearer ${tok}`);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = { error: res.statusText };
    }
    const err = new Error(`${res.status}: ${JSON.stringify(body)}`) as Error & {
      status: number;
      body: unknown;
    };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json() as Promise<T>;
}

/** Base URL of the API; exposed for callers that bypass `request` (e.g. multipart upload). */
export { BASE };

export const api = {
  login(body: {
    username: string;
    password: string;
    totp?: string;
    rememberDevice?: boolean;
  }): Promise<LoginResponse> {
    // credentials:'include' so the trusted-device cookie flows both ways.
    return request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
      credentials: 'include',
    });
  },
  async logout(): Promise<void> {
    // Best-effort: if the server is unreachable we still clear local state.
    try {
      await fetch(`${BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // ignore
    }
  },
  me(): Promise<{ user: { sub: string; username: string; role: string } }> {
    return request('/auth/me');
  },
  setKeypair(body: { publicKey: string; encryptedPrivateKey: EncryptedPrivateKey }) {
    return request<{ ok: true }>('/auth/keypair', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  /**
   * Bootstrap the shared vault. Called from `setupMasterCode` for the very
   * first user on a fresh install. Still lives in Core because it's driven
   * from the Core session store during Master Code setup; moves to
   * `apps/vault/lib/api.ts` once Master Code setup splits into a Core identity
   * flow + a vault-specific bootstrap flow.
   */
  bootstrapVault(wrappedKey: unknown) {
    return request<{ ok: true }>('/auth/vault/bootstrap', {
      method: 'POST',
      body: JSON.stringify({ wrappedKey }),
    });
  },
  /**
   * Current session state for the authenticated user: Core identity + vault
   * login state. Called on page refresh to rebuild the store from the
   * persisted JWT without redoing password + TOTP.
   */
  myEnrolment(): Promise<{
    user: {
      id: string;
      username: string;
      email: string;
      role: string;
      createdAt: string;
    } | null;
    encryptedPrivateKey: EncryptedPrivateKey | null;
    vaultState: VaultLoginState | null;
  }> {
    return request('/auth/me/enrolment');
  },
  /** Admin audit-log reader. Tail recent actions, newest-first. */
  audit(limit = 200) {
    return request<{ entries: Array<Record<string, unknown>> }>(
      `/api/audit?limit=${limit}`,
    );
  },
};
